import React, { useEffect, useState } from 'react';
import { string, oneOf, oneOfType, number, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useStyles from 'hooks/useStyles';
import { COURSE, REGULAR_CLASS } from 'constants/contentTypes';
import { SUBSCRIPTION, SEMESTER } from 'constants/checkoutCollectionGroupTypes';
import { SINGLE, BUNDLES, PREPACK } from 'constants/checkoutCollectionTypes';

import {
  fetchCheckoutCollectionList,
  fetchLiveTopicMainCheckoutCollection,
} from 'reduxModules/checkoutCollection/action';

import {
  getContentCheckoutCollectionList,
  getContentMainCheckoutCollection,
} from 'reduxModules/checkoutCollection/selector';

import Button from 'components/Button';
import FullMask from 'components/FullMask';
import { CloseIcon } from 'components/Icon';

import PaymentOptionGroup from './components/PaymentOptionGroup';
import PaymentOptionEnhanced from './components/PaymentOptionEnhanced';
import PaymentBundleOptionEnhanced from './components/PaymentBundleOptionEnhanced';
import PaymentPrepackOption from './components/PaymentPrepackOption';
import PaymentOptionContext from './context/PaymentOptionContext';

import styles from './PaymentOptionList.scss';

const checkPaymentOptions = paymentOptions => {
  const checkIndex = paymentOptions.hasAddOn !== undefined ? 1 : 0;

  return Object.keys(paymentOptions).length > checkIndex;
};

const renderOptionGroup = ({
  paymentOptionGroupList,
  groupTitleMap,
  contentType,
  contentId,
  topicId,
  topicName,
}) =>
  Object.entries(paymentOptionGroupList).map(([groupType, groupOptionList]) => (
    <PaymentOptionGroup key={groupType} title={groupTitleMap[groupType]}>
      {Object.entries(groupOptionList).map(([optionType, option]) => {
        if (Object.keys(option).length === 0 || option.length === 0)
          return null;

        switch (optionType) {
          case SINGLE: {
            return (
              <PaymentOptionEnhanced
                key={option.id}
                contentType={contentType}
                contentId={contentId}
                groupType={groupType}
                lessonCount={option.lessons.length}
                isSemester={option.isSemester}
                topicId={topicId}
                topicName={topicName}
                {...option}
              />
            );
          }
          case BUNDLES: {
            const hasSingleOption =
              Object.keys(groupOptionList[SINGLE]).length > 0;

            return option.map((bundleOption, bundleIndex) => (
              <PaymentBundleOptionEnhanced
                optionList={bundleOption}
                // eslint-disable-next-line react/no-array-index-key
                key={`bundle-${bundleIndex}`}
                contentType={contentType}
                contentId={contentId}
                groupType={groupType}
                shouldShowDetail={!hasSingleOption}
              />
            ));
          }
          case PREPACK:
            return option.map(prepackOption => (
              <PaymentPrepackOption
                key={prepackOption.id}
                contentType={contentType}
                contentId={contentId}
                groupType={groupType}
                {...prepackOption}
              />
            ));
          default:
            return null;
        }
      })}
    </PaymentOptionGroup>
  ));

export default function PaymentOptionList({
  contentType,
  contentId,
  onClickPaymentOption,
  onClickCtaButton,
  topicId,
  topicName,
}) {
  useStyles(styles);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation(['purchase']);

  const dispatch = useDispatch();

  const mainPaymentOption = useSelector(
    getContentMainCheckoutCollection(contentType, contentId),
  );

  const paymentOptionGroupList = useSelector(
    getContentCheckoutCollectionList(contentType, contentId),
  );

  const groupTitleMap = {
    [SUBSCRIPTION]: t('purchase.content.subscriptions.title'),
    [SEMESTER]: t('purchase.content.semesterPlan.title'),
  };

  useEffect(() => {
    dispatch(fetchCheckoutCollectionList({ contentType, contentId }));
  }, [dispatch, contentType, contentId]);

  useEffect(() => {
    if (contentType !== REGULAR_CLASS) return;
    dispatch(fetchLiveTopicMainCheckoutCollection(contentId));
  }, [dispatch, contentType, contentId]);

  const toggleModal = () => setShowModal(state => !state);

  const handleClickCtaButton = () => {
    onClickCtaButton();
    toggleModal();
  };

  const havePaymentOption = checkPaymentOptions(mainPaymentOption);

  return (
    <PaymentOptionContext.Provider value={{ onClickPaymentOption }}>
      {havePaymentOption && (
        <div className={styles.toggler}>
          <Button
            text={t('purchase:purchase.content.cta.purchase', {
              price: mainPaymentOption.displayPrice?.sellingPrice,
            })}
            block
            onClick={handleClickCtaButton}
          />
        </div>
      )}
      {showModal ? (
        <FullMask>
          <div className={styles.modalWrapper}>
            <div className={styles.header}>
              <CloseIcon
                width={24}
                height={24}
                onClick={toggleModal}
                className={styles.closeIcon}
              />
              <h4>{t('purchase.content.pricingPlanTitle')}</h4>
            </div>
            {renderOptionGroup({
              paymentOptionGroupList,
              groupTitleMap,
              contentType,
              contentId,
              topicId,
              topicName,
            })}
          </div>
        </FullMask>
      ) : (
        <div className={styles.wrapper}>
          {renderOptionGroup({
            paymentOptionGroupList,
            groupTitleMap,
            contentType,
            contentId,
            topicId,
            topicName,
          })}
        </div>
      )}
    </PaymentOptionContext.Provider>
  );
}

PaymentOptionList.propTypes = {
  contentType: oneOf([COURSE, REGULAR_CLASS]).isRequired,
  contentId: oneOfType([string, number]).isRequired,
  topicId: number.isRequired,
  topicName: string.isRequired,
  onClickPaymentOption: func,
  onClickCtaButton: func,
};

PaymentOptionList.defaultProps = {
  onClickPaymentOption: () => {},
  onClickCtaButton: () => {},
};
